import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import agentService from '../services/agent.service';
import { parseSkillAccessDenied } from '../utils/parseSkillAccessDenied';
import type {
  AgentMessage,
  AgentConversationListItem,
  ConversationStatus,
  ConfirmActionResponse,
} from '../types/agent.types';
import { showToast } from '../utils/toast';

interface AgentChatContextType {
  // Panel-level state (shared with SupportChat via ChatPanel)
  activeTab: 'support' | 'agent' | 'agents-iframe';
  setActiveTab: (tab: 'support' | 'agent' | 'agents-iframe') => void;

  // Conversation state
  conversationId: number | null;
  conversationStatus: ConversationStatus | null;
  currentProvider: string | null;
  messages: AgentMessage[];
  isLoading: boolean;
  loadingElapsed: number;

  // Confirmation
  pendingConfirmation: string | null;
  lastConfirmResult: ConfirmActionResponse | null;

  // Actions
  sendMessage: (content: string, overrideConvId?: number) => Promise<void>;
  confirmAction: () => Promise<void>;
  cancelAction: () => Promise<void>;
  startNewConversation: () => Promise<number | null>;
  endConversation: () => Promise<void>;
  loadConversation: (id: number) => Promise<void>;

  // Conversation list
  conversations: AgentConversationListItem[];
  conversationsLoading: boolean;
  loadConversations: () => Promise<void>;

  // View
  agentView: 'chat' | 'list';
  setAgentView: (view: 'chat' | 'list') => void;
}

const AgentChatContext = createContext<AgentChatContextType | null>(null);

export const AgentChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Tab state (shared between Support and Agent)
  const [activeTab, setActiveTab] = useState<'support' | 'agent' | 'agents-iframe'>('support');

  // Conversation
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [conversationStatus, setConversationStatus] = useState<ConversationStatus | null>(null);
  const [currentProvider, setCurrentProvider] = useState<string | null>(null);
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingElapsed, setLoadingElapsed] = useState(0);

  // Confirmation
  const [pendingConfirmation, setPendingConfirmation] = useState<string | null>(null);
  const [lastConfirmResult, setLastConfirmResult] = useState<ConfirmActionResponse | null>(null);

  // Conversation list
  const [conversations, setConversations] = useState<AgentConversationListItem[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // View
  const [agentView, setAgentView] = useState<'chat' | 'list'>('chat');

  // Refs for elapsed timer, temp IDs, and creation guard
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadingStartRef = useRef(0);
  const tempIdRef = useRef(0);
  const isCreatingRef = useRef(false);

  const startLoadingTimer = useCallback(() => {
    setIsLoading(true);
    setLoadingElapsed(0);
    loadingStartRef.current = Date.now();
    elapsedTimerRef.current = setInterval(() => {
      setLoadingElapsed(Date.now() - loadingStartRef.current);
    }, 1000);
  }, []);

  const stopLoadingTimer = useCallback(() => {
    setIsLoading(false);
    setLoadingElapsed(0);
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  // Cleanup interval on unmount to prevent memory leaks
  useEffect(() => () => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  const startNewConversation = useCallback(async (): Promise<number | null> => {
    if (isCreatingRef.current) return null;
    isCreatingRef.current = true;
    try {
      const conv = await agentService.createConversation();
      setConversationId(conv.id);
      setConversationStatus(conv.status);
      setCurrentProvider(conv.current_provider);
      setMessages([]);
      setPendingConfirmation(null);
      setLastConfirmResult(null);
      setAgentView('chat');
      return conv.id;
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Не удалось создать диалог', 'error');
      return null;
    } finally {
      isCreatingRef.current = false;
    }
  }, []);

  const sendMessage = useCallback(async (content: string, overrideConvId?: number) => {
    const convId = overrideConvId ?? conversationId;
    if (!convId || isLoading) return;

    // Auto-clear previous confirm result
    setLastConfirmResult(null);

    // Optimistic: show user message immediately
    const tempId = --tempIdRef.current;
    setMessages(prev => [...prev, {
      id: tempId,
      conversation_id: convId,
      role: 'user' as const,
      content,
      tool_calls: null,
      tool_results: null,
      tokens_used: 0,
      model_used: null,
      created_at: new Date().toISOString(),
    }]);

    startLoadingTimer();
    try {
      const resp = await agentService.sendMessage(convId, content);
      // Replace temp message with real one + add assistant response
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempId),
        resp.user_message,
        resp.assistant_message,
      ]);

      if (resp.requires_confirmation && resp.confirmation_preview) {
        setPendingConfirmation(resp.confirmation_preview);
      }

      // Update provider from assistant message context
      if (resp.assistant_message.tool_calls?.length) {
        // Provider may have changed after tool calls
      }
    } catch (err) {
      // Без удаления — оптимистичное сообщение останется без ответа агента.
      setMessages(prev => prev.filter(m => m.id !== tempId));
      const detail = err instanceof Error ? err.message : 'Ошибка отправки';
      const denied = parseSkillAccessDenied(detail);
      if (denied) {
        const cmd = denied.skillCode ? `/${denied.skillCode}` : 'этой команде';
        showToast(
          `У вас нет доступа к команде ${cmd}. Запросите доступ у администратора.`,
          'warning',
        );
      } else {
        showToast(detail, 'error');
      }
    } finally {
      stopLoadingTimer();
    }
  }, [conversationId, isLoading, startLoadingTimer, stopLoadingTimer]);

  const confirmAction = useCallback(async () => {
    if (!conversationId) return;
    startLoadingTimer();
    try {
      const result = await agentService.confirmAction(conversationId);
      setPendingConfirmation(null);
      setLastConfirmResult(result);

      // Reload conversation to get all messages including tool_results and AI continuation
      const conv = await agentService.getConversation(conversationId);
      setMessages(conv.messages ?? []);
      setCurrentProvider(conv.current_provider);
      setConversationStatus(conv.status);

      // Check if there's a new pending confirmation
      if (conv.pending_confirmation) {
        setPendingConfirmation(
          typeof conv.pending_confirmation === 'string'
            ? conv.pending_confirmation
            : JSON.stringify(conv.pending_confirmation, null, 2)
        );
      }

      showToast(result.message, 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Ошибка подтверждения', 'error');
    } finally {
      stopLoadingTimer();
    }
  }, [conversationId, startLoadingTimer, stopLoadingTimer]);

  const cancelAction = useCallback(async () => {
    if (!conversationId) return;
    try {
      const result = await agentService.cancelAction(conversationId);
      setPendingConfirmation(null);
      setLastConfirmResult(null);

      setMessages(prev => [...prev, {
        id: Date.now(),
        conversation_id: conversationId,
        role: 'assistant' as const,
        content: result.message,
        tool_calls: null,
        tool_results: null,
        tokens_used: 0,
        model_used: null,
        created_at: new Date().toISOString(),
      }]);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Не удалось отменить', 'error');
    }
  }, [conversationId]);

  const endConversation = useCallback(async () => {
    if (!conversationId) return;
    try {
      await agentService.deleteConversation(conversationId);
      setConversationStatus('completed');
      setPendingConfirmation(null);
      showToast('Диалог завершён', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Не удалось завершить', 'error');
    }
  }, [conversationId]);

  const loadConversation = useCallback(async (id: number) => {
    try {
      const conv = await agentService.getConversation(id);
      setConversationId(conv.id);
      setConversationStatus(conv.status);
      setCurrentProvider(conv.current_provider);
      setPendingConfirmation(
        typeof conv.pending_confirmation === 'string'
          ? conv.pending_confirmation
          : conv.pending_confirmation
            ? JSON.stringify(conv.pending_confirmation, null, 2)
            : null
      );
      setLastConfirmResult(null);
      setMessages(conv.messages ?? []);
      setAgentView('chat');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Не удалось загрузить диалог', 'error');
    }
  }, []);

  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const resp = await agentService.listConversations();
      setConversations(resp.items);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Не удалось загрузить диалоги', 'error');
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  return (
    <AgentChatContext.Provider value={{
      activeTab,
      setActiveTab,
      conversationId,
      conversationStatus,
      currentProvider,
      messages,
      isLoading,
      loadingElapsed,
      pendingConfirmation,
      lastConfirmResult,
      sendMessage,
      confirmAction,
      cancelAction,
      startNewConversation,
      endConversation,
      loadConversation,
      conversations,
      conversationsLoading,
      loadConversations,
      agentView,
      setAgentView,
    }}>
      {children}
    </AgentChatContext.Provider>
  );
};

export const useAgentChat = (): AgentChatContextType => {
  const ctx = useContext(AgentChatContext);
  if (!ctx) throw new Error('useAgentChat must be used within AgentChatProvider');
  return ctx;
};
